'use client';

import React from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import type { FilterState } from '@/lib/types';

interface Props {
  filters: FilterState;
  onFiltersChange: (f: FilterState) => void;
}

const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'continuing', label: 'Continuing' },
  { value: 'ended', label: 'Ended' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'available', label: 'Available' },
  { value: 'announced', label: 'Announced' },
  { value: 'in-cinemas', label: 'In Cinemas' },
];

const genreOptions = [
  { value: 'all', label: 'All Genres' },
  { value: 'Drama', label: 'Drama' },
  { value: 'Action', label: 'Action' },
  { value: 'Comedy', label: 'Comedy' },
  { value: 'Thriller', label: 'Thriller' },
  { value: 'Sci-Fi', label: 'Sci-Fi' },
  { value: 'Horror', label: 'Horror' },
  { value: 'Crime', label: 'Crime' },
  { value: 'Adventure', label: 'Adventure' },
  { value: 'Animation', label: 'Animation' },
  { value: 'Documentary', label: 'Documentary' },
];

export default function MediaFilters({ filters, onFiltersChange }: Props) {
  const hasActiveFilters =
    filters.search !== '' || filters.status !== 'all' || filters.genre !== 'all';

  const reset = () =>
    onFiltersChange({ search: '', status: 'all', genre: 'all', source: 'all' });

  return (
    <div className="flex flex-wrap items-center gap-2 py-3">
      {/* Search */}
      <div className="relative flex-1 min-w-[200px] max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search titles..."
          value={filters.search}
          onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
          className="input-base w-full pl-8 pr-3 py-1.5 text-sm"
        />
        {filters.search && (
          <button
            onClick={() => onFiltersChange({ ...filters, search: '' })}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X size={12} />
          </button>
        )}
      </div>

      {/* Status filter */}
      <div className="relative">
        <select
          value={filters.status}
          onChange={(e) => onFiltersChange({ ...filters, status: e.target.value })}
          className="input-base pl-3 pr-8 py-1.5 text-sm appearance-none cursor-pointer"
        >
          {statusOptions.map((o) => (
            <option key={`status-${o.value}`} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <SlidersHorizontal
          size={12}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
        />
      </div>

      {/* Genre filter */}
      <div className="relative">
        <select
          value={filters.genre}
          onChange={(e) => onFiltersChange({ ...filters, genre: e.target.value })}
          className="input-base pl-3 pr-8 py-1.5 text-sm appearance-none cursor-pointer"
        >
          {genreOptions.map((o) => (
            <option key={`genre-${o.value}`} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <SlidersHorizontal
          size={12}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
        />
      </div>

      {/* Reset */}
      {hasActiveFilters && (
        <button
          onClick={reset}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium btn-ghost text-muted-foreground"
        >
          <X size={13} />
          Reset filters
        </button>
      )}
    </div>
  );
}